/**
 * PoolBoardingPassNotice — « votre véhicule est réservé » (2026-09-14).
 * =============================================================================
 * La remise en main de la carte d'embarquement : la notification in-app créée
 * par le guichet (`type = 'pool_boarding_pass'`) se lit ici, en tête de
 * l'accueil conducteur, tant qu'elle n'est pas lue. Un clic sur « J'ai vu » la
 * marque lue côté serveur — c'est le seul geste, pas de centre de notifications.
 *
 * Sondage toutes les 30 s : pas de push (voie (a) du cadrage du 14/09 — zéro
 * infrastructure). Le vrai push (Firebase ou local Android) viendra après.
 *
 * Erreur silencieuse : l'accueil reste lisible sans le service de notifications.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Ticket, Check } from 'lucide-react';
import { notificationAPI } from '../../../api/notifications.js';

export const BOARDING_PASS_TYPE = 'pool_boarding_pass';

export function PoolBoardingPassNotice({ onOpen = null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationAPI.list({ unreadOnly: true, limit: 20 }),
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (id) => notificationAPI.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  const notice = rows.find((n) => n.type === BOARDING_PASS_TYPE && !n.read_at) || null;
  if (!notice) return null;

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900"
      data-testid="boarding-pass-notice"
    >
      <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{notice.title || t('poolDriver.boardingPass.noticeTitle')}</p>
        {notice.message && <p className="mt-0.5 text-emerald-800">{notice.message}</p>}
        <div className="mt-1.5 flex flex-wrap gap-2">
          {onOpen && (
            <button
              type="button"
              onClick={() => onOpen(notice)}
              className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
            >
              {t('poolDriver.boardingPass.noticeOpen')}
            </button>
          )}
          <button
            type="button"
            onClick={() => markRead.mutate(notice.id)}
            disabled={markRead.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
          >
            <Check className="h-3.5 w-3.5" />
            {t('poolDriver.boardingPass.noticeSeen')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PoolBoardingPassNotice;
